/** Pure helpers for the manager's PRIVATE chat with the agent (P5.4 / EVAS-14). Kept free of React and
 * livekit-client so they unit-test without a browser (mirrors participants.ts). */

import { PRIVATE_CHAT_MAX_TEXT, PrivateChatMessage } from "@evas/shared";

/** One line in the private thread. `id` is local-only (the thread is not persisted client-side; the
 * agent records both sides to the transcript). `briefing` marks the EVAS-117 line the manager did not
 * ask for — labeled and styled differently, because "here is what you walked into" reads nothing like
 * an answer to a question. */
export interface PrivateLine {
  id: string;
  from: "manager" | "agent";
  text: string;
  briefing?: boolean;
}

/** Keep the private thread bounded — it's a side panel, not the session record. */
export const MAX_PRIVATE_LINES = 50;

/** Append a line, trimming to the last {@link MAX_PRIVATE_LINES}. Pure — never mutates the input. */
export function appendPrivateLine(list: readonly PrivateLine[], line: PrivateLine): PrivateLine[] {
  const next = [...list, line];
  return next.length > MAX_PRIVATE_LINES ? next.slice(next.length - MAX_PRIVATE_LINES) : next;
}

/** The agent's identity in the room, so a private question can be addressed to it via
 * `destinationIdentities`. The LiveKit job worker joins as `agent-<id>` and IS the participant running
 * our code (it owns the text-stream topics); the LiveAvatar participant
 * (`liveavatar-avatar-agent`) only publishes media and would never see the message. Returns undefined
 * when the worker hasn't joined yet, so the UI can disable the private composer. */
export function findAgentIdentity(identities: readonly string[]): string | undefined {
  return identities.find((id) => id.startsWith("agent-"));
}

/** One inbound line from the agent: the text plus whether it was UNSOLICITED (the EVAS-117 join
 * briefing). The distinction is not cosmetic — an answer belongs in a panel the manager already opened,
 * a briefing has to OPEN that panel or nobody reads it. */
export interface AgentPrivateLine {
  text: string;
  briefing: boolean;
}

/** Parse an inbound private-chat payload, returning the agent's line or null when the frame is
 * malformed / not addressed to the manager (e.g. our own `toAgent` echo). */
export function parseAgentReply(raw: string): AgentPrivateLine | null {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  const parsed = PrivateChatMessage.safeParse(json);
  if (!parsed.success || parsed.data.direction !== "toManager") return null;
  const text = parsed.data.text.trim();
  if (!text) return null;
  return { text, briefing: parsed.data.kind === "briefing" };
}

/** Build the outbound frame for a manager's private question, or null when the text is empty. */
export function buildPrivateQuestion(text: string): PrivateChatMessage | null {
  const body = text.trim();
  if (!body) return null;
  // The contract caps length; truncate rather than reject so a long paste still sends.
  return { v: 1, direction: "toAgent", text: body.slice(0, PRIVATE_CHAT_MAX_TEXT) };
}
